import type { FC, ReactNode } from 'react'
import type { GaleriaItem } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { ArrowRight, Expand } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { buttonVariants } from '@/components/ui/button'
import AppImage from '@/components/generals/app-image'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

interface GalleryGridProps {
    items: Array<GaleriaItem>
    limit?: number
    showFilters?: boolean
}

const ALL = 'todas'

const chipClass = (active: boolean): string => cn(
    'rounded-sm border px-4 py-2.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] transition-colors',
    active ? 'border-brass bg-brass text-black' : 'border-ivory/15 text-ivory/75 hover:border-brass/60 hover:text-brass'
)

const GalleryGrid: FC<GalleryGridProps> = ({ items, limit, showFilters = true }): ReactNode => {
    const [filtro, setFiltro] = useState<string>(ALL)
    const [activo, setActivo] = useState<GaleriaItem | null>(null)

    const ocasiones = useMemo(() => Array.from(new Set(items.map(item => item.ocasion))), [items])
    const filtrados = filtro === ALL ? items : items.filter(item => item.ocasion === filtro)
    const visibles = limit ? filtrados.slice(0, limit) : filtrados

    if (items.length === 0) {
        return (
            <div className='rounded-sm border border-dashed border-ivory/15 px-6 py-16 text-center text-muted-foreground'>
                Muy pronto compartiremos nuevos ejemplos de inspiración.
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-10'>
            {showFilters && ocasiones.length > 1 && (
                <div role='group' aria-label='Filtrar ejemplos por ocasión' className='flex flex-wrap gap-2'>
                    <button type='button' aria-pressed={filtro === ALL} onClick={() => setFiltro(ALL)} className={chipClass(filtro === ALL)}>
                        Todas
                    </button>

                    {ocasiones.map(ocasion => (
                        <button
                            key={ocasion}
                            type='button'
                            aria-pressed={filtro === ocasion}
                            onClick={() => setFiltro(ocasion)}
                            className={chipClass(filtro === ocasion)}
                        >
                            {ocasion}
                        </button>
                    ))}
                </div>
            )}

            <p aria-live='polite' className='sr-only'>
                {visibles.length} {visibles.length === 1 ? 'ejemplo visible' : 'ejemplos visibles'}
            </p>

            <ul className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                {visibles.map(item => (
                    <li key={item.id}>
                        <article className='group relative grid overflow-hidden rounded-sm border border-ivory/10 bg-graphite transition-colors duration-500 hover:border-brass/50'>
                            <div className='relative aspect-[4/3] overflow-hidden [grid-area:1/1]'>
                                <AppImage
                                    src={item.imagen}
                                    alt={item.imagen_alt}
                                    sizes='(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw'
                                    className='transition-transform duration-700 ease-out group-hover:scale-105 motion-reduce:transition-none'
                                />

                                <div aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-black/90 via-black/20 to-transparent' />

                                <span aria-hidden='true' className='absolute top-4 right-4 inline-flex size-9 items-center justify-center rounded-sm border border-ivory/20 bg-black/60 text-ivory opacity-0 transition-opacity duration-300 group-hover:opacity-100'>
                                    <Expand className='size-4' />
                                </span>
                            </div>

                            <div className='z-10 flex flex-col gap-2 self-end p-6 [grid-area:1/1]'>
                                <p className='text-[0.62rem] font-semibold uppercase tracking-[0.26em] text-brass'>
                                    <span data-mdlfy-cms={cms('galeria', item.id, 'ocasion')}>{item.ocasion}</span>
                                </p>

                                <h3 className='font-heading text-2xl leading-tight font-medium text-foreground'>
                                    <button
                                        type='button'
                                        onClick={() => setActivo(item)}
                                        className='text-left outline-none after:absolute after:inset-0 after:content-[""] focus-visible:after:outline-2 focus-visible:after:outline-offset-[-4px] focus-visible:after:outline-brass'
                                    >
                                        <span data-mdlfy-cms={cms('galeria', item.id, 'titulo')}>{item.titulo}</span>
                                        <span className='sr-only'>, ver en grande</span>
                                    </button>
                                </h3>
                            </div>
                        </article>
                    </li>
                ))}
            </ul>

            {visibles.length === 0 && (
                <p className='text-center text-muted-foreground'>No hay ejemplos para esta ocasión todavía.</p>
            )}

            <Dialog open={activo !== null} onOpenChange={open => { if (!open) setActivo(null) }}>
                {activo && (
                    <DialogContent>
                        <div className='grid md:grid-cols-5'>
                            <div className='relative aspect-[4/3] bg-black md:col-span-3 md:aspect-auto md:min-h-[28rem]'>
                                <AppImage src={activo.imagen} alt={activo.imagen_alt} sizes='(min-width: 768px) 60vw, 100vw' />
                            </div>

                            <div className='flex flex-col gap-5 p-7 md:col-span-2 md:p-9'>
                                <p className='text-[0.62rem] font-semibold uppercase tracking-[0.26em] text-brass'>
                                    <span data-mdlfy-cms={cms('galeria', activo.id, 'ocasion')}>{activo.ocasion}</span>
                                </p>

                                <DialogTitle>
                                    <span data-mdlfy-cms={cms('galeria', activo.id, 'titulo')}>{activo.titulo}</span>
                                </DialogTitle>

                                {activo.descripcion && (
                                    <DialogDescription>
                                        <span data-mdlfy-cms={cms('galeria', activo.id, 'descripcion')}>{activo.descripcion}</span>
                                    </DialogDescription>
                                )}

                                <div className='mt-auto flex flex-col gap-3 pt-4'>
                                    <Link
                                        href={activo.categoria ? `/cotizacion?categoria=${activo.categoria.slug}` : '/cotizacion'}
                                        className={buttonVariants({ size: 'sm' })}
                                    >
                                        Solicitar algo similar
                                        <ArrowRight aria-hidden='true' />
                                    </Link>

                                    {activo.categoria && (
                                        <Link href={`/catalogo/${activo.categoria.slug}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                                            Ver categoría <span data-mdlfy-cms={cms('categorias', activo.categoria.id, 'nombre')}>{activo.categoria.nombre}</span>
                                        </Link>
                                    )}
                                </div>

                                <p className='text-xs leading-relaxed text-ivory/50'>
                                    Imagen de referencia con fines demostrativos.
                                </p>
                            </div>
                        </div>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    )
}

export default GalleryGrid
